import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { Store, createSelector } from '@ngrx/store';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import {
  CUSTOMER_TYPES,
  DEFAULT_HOW_HEARD_DROPDOWN_OPTIONS,
  JOB_CATEGORIES,
  JOB_ORIGINS,
} from 'src/app/global.constants';
import { MrCalculatorComponent } from 'src/app/job/job-workorders/mr-calculator/mr-calculator.component';
import { LibModule } from 'src/app/lib.ts/lib.module';
import { ControlMetaDirective } from 'src/app/new-opportunity/control-meta.directive';
import { quickjsFeature } from 'src/app/quickjs/quickjs.feature';
import { GoogleHelperService } from 'src/app/services/google-helper.service';
import { createConfigSelector } from 'src/app/state/branding.store';
import { LoadingState } from 'src/app/utilities/state.util';

import { jobCreateCustomerFeature } from '../../job/job-create/job-create-customer/job-create-customer.feature';
import { EMAIL_PATTERN, PHONE_PATTERN } from '../../job/job-helpers';
import { overviewLocationsFeature } from '../../job/job-overview/overview-locations/overview-locations.feature';
import { overviewTimelineFeature } from '../../job/job-overview/overview-timeline/overview-timeline.feature';
import { selectTimelineRange } from '../../job/job-overview/overview-timeline/overview-timeline.selectors';
import { selectCalculatorUrl, selectCustomerEditable } from '../../job/job-tool.reducer';

import { AvailabilityScheduleComponent } from '../../shared/availability-schedule/availability-schedule.component';
import { SourceTreeSelectComponent } from '../../shared/source-tree-select/source-tree-select.component';

import { NewOpportunitySearchCustomersDialogActions } from '../new-opportunity-search-customers-dialog/new-opportunity-search-customers-dialog.actions';
import { NewOpportunitySearchCustomersDialogComponent } from '../new-opportunity-search-customers-dialog/new-opportunity-search-customers-dialog.component';
import { NewOpportunitySimilarJobsComponent } from '../new-opportunity-similar-jobs/new-opportunity-similar-jobs.component';

import { AvailabilityDateComponent } from './availability-date/availability-date.component';
import { NewOpportunityCustomerActions } from './new-opportunity-customer.actions';

@Component({
  selector: 'app-new-opportunity-customer',
  standalone: true,
  imports: [
    FreyaCommonModule,
    AvailabilityScheduleComponent,
    AvailabilityDateComponent,
    NgxGpAutocompleteModule,
    MrCalculatorComponent,
    NewOpportunitySimilarJobsComponent,
    NewOpportunitySearchCustomersDialogComponent,
    ControlMetaDirective,
    LibModule,
    SourceTreeSelectComponent,
  ],
  templateUrl: './new-opportunity-customer.component.html',
  styleUrls: ['./new-opportunity-customer.component.scss'],
})
export class NewOpportunityCustomerComponent implements AfterViewInit, OnDestroy {
  @ViewChild(AvailabilityScheduleComponent) availabilitySchedule?: AvailabilityScheduleComponent;

  scheduleHeight: number | null = null;

  private resizeObserver?: ResizeObserver;

  jobCategories: string[] = JOB_CATEGORIES;
  readonly emailPattern = EMAIL_PATTERN;
  readonly phonePattern = PHONE_PATTERN;

  viewModel$ = this.store.select(
    createSelector({
      fullName: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.fullName,
      ),
      email: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.email,
      ),
      phone: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.phone,
      ),
      company: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.company,
      ),
      additionalContacts: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.additionalContacts.filter((c) => !c.isUnlinked),
      ),
      selectedCustomer: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.selectedCustomer,
      ),
      isCustomerModified: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.isCustomerModified,
      ),
      customerType: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.customerType,
      ),
      referralSource: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.referralSource,
      ),
      jobOrigin: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => editor.jobOrigin,
      ),
      customerEditable: selectCustomerEditable,
      showCalculator: createSelector(
        selectCalculatorUrl,
        quickjsFeature.selectCustomerElements,
        quickjsFeature.selectScriptLoaded,
        (calculatorUrl, customerElements, loaded) => Boolean(
          calculatorUrl
          && (!loaded || customerElements?.length)
        )
      ),
      howHeardDropdownOptions: createSelector(
        createConfigSelector(
          'JOBS_HOWHEARDOPTIONS',
          DEFAULT_HOW_HEARD_DROPDOWN_OPTIONS,
        ),
        jobCreateCustomerFeature.selectCustomerEditor,
        (options, editor) =>
          editor.referralSource && !options.includes(editor.referralSource)
            ? [...options, editor.referralSource]
            : options,
      ),
      howHeardMandatory: createConfigSelector('JOBS_HOWHEARDMANDATORY'),
      customerTypeOptions: createSelector(
        createConfigSelector('CUSTOMER_TYPES', CUSTOMER_TYPES),
        jobCreateCustomerFeature.selectCustomerEditor,
        (options, editor) =>
          editor.customerType && !options.includes(editor.customerType)
            ? [...options, editor.customerType]
            : options,
      ),
      jobOriginOptions: createSelector(
        createConfigSelector('JOBS_ORIGINS', JOB_ORIGINS),
        jobCreateCustomerFeature.selectCustomerEditor,
        (options, editor) =>
          editor.jobOrigin && !options.includes(editor.jobOrigin)
            ? [...options, editor.jobOrigin]
            : options,
      ),
      availabilityDates: selectTimelineRange,
      showEmptyState: createSelector(
        selectTimelineRange,
        (timelineRange) => timelineRange.length === 0,
      ),
      showLoadingState: createSelector(
        selectTimelineRange,
        overviewTimelineFeature.selectViewDatesLoading,
        overviewTimelineFeature.selectViewDatesMap,
        (timelineRange, viewDatesLoading, viewDatesMap) => {
          if (timelineRange.length === 0) {
            return false;
          }
          const isLoading =
            viewDatesLoading === LoadingState.INIT ||
            viewDatesLoading === LoadingState.LOADING;
          return isLoading && timelineRange.some((date) => !viewDatesMap[date]);
        },
      ),
      showAvailabilityDates: createSelector(
        selectTimelineRange,
        overviewTimelineFeature.selectViewDatesLoading,
        overviewTimelineFeature.selectViewDatesMap,
        (timelineRange, viewDatesLoading, viewDatesMap) => {
          if (timelineRange.length === 0) {
            return false;
          }
          const isLoading =
            viewDatesLoading === LoadingState.INIT ||
            viewDatesLoading === LoadingState.LOADING;
          const showLoadingState =
            isLoading && timelineRange.some((date) => !viewDatesMap[date]);
          return !showLoadingState;
        },
      ),
      locationEditors: overviewLocationsFeature.selectLocationEditors,
      useEndLocationAsBillingLocation: overviewLocationsFeature.selectUseEndLocationAsBillingLocation,
      tabIndices: createSelector(
        jobCreateCustomerFeature.selectCustomerEditor,
        (editor) => {
          const contactCount = editor.additionalContacts?.length ?? 0;
          const offset = contactCount * 4;
          return {
            fullName: 1,
            company: 2,
            email: 3,
            phone: 4,
            additionalContacts: (editor.additionalContacts ?? []).map((_, i) => ({
              name: 5 + i * 4,
              email: 6 + i * 4,
              phone: 7 + i * 4,
              relationship: 8 + i * 4,
            })),
            addContact: 5 + offset,
            customerType: 6 + offset,
            howHeard: 7 + offset,
            jobOrigin: 8 + offset,
            billingCheckbox: 9 + offset,
            billingUnit: 10 + offset,
            billingAddress: 11 + offset,
          };
        },
      ),
      billingLocationInvalid: createSelector(
        overviewLocationsFeature.selectLocationEditors,
        (editors) => !editors?.billing?.selectedLocation,
      ),
    }),
  );

  actions = NewOpportunityCustomerActions;
  searchCustomersDialogActions = NewOpportunitySearchCustomersDialogActions;

  constructor(
    public store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    public googleHelper: GoogleHelperService,
  ) {}

  focusBillingAddress(checked: boolean): void {
    if (checked) {
      requestAnimationFrame(() => {
        document.getElementById('billingAddress')?.focus();
      });
    }
  }

  ngAfterViewInit(): void {
    this.watchSchedule();
  }

  watchSchedule() {
    const scheduleElement = this.availabilitySchedule?.el?.nativeElement;
    if (!scheduleElement || !('ResizeObserver' in window)) {
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nextHeight = Math.round(entry.contentRect.height);
        if (nextHeight === this.scheduleHeight) {
          continue;
        }
        this.ngZone.run(() => {
          this.scheduleHeight = nextHeight;
          this.changeDetectorRef.markForCheck();
        });
      }
    });

    this.resizeObserver.observe(scheduleElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  trackByIndex(index: number) {
    return index;
  }
}
